import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  ImagePlus,
  LineChart,
  Mail,
  Save,
  ShieldAlert,
  Trash2,
  UserPlus,
  UserRound,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Masthead } from '@/components/Masthead';
import {
  clearSettings,
  loadSettings,
  saveSettings,
  type PaymentMethod,
  type UserProfile,
  type UserSettings,
  type UserSignature,
} from '@/lib/settings';
import {
  addInvite,
  getInvites,
  getSession,
  isOwner,
  isValidEmail,
  OWNER_EMAIL,
  removeInvite,
} from '@/lib/auth';
import { cn } from '@/lib/cn';

export default function Settings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<UserSettings>(() => loadSettings());
  const [status, setStatus] = useState<
    { kind: 'idle' } | { kind: 'saved'; section: string } | { kind: 'error'; msg: string }
  >({ kind: 'idle' });

  const flash = (section: string) => {
    setStatus({ kind: 'saved', section });
    window.setTimeout(() => setStatus({ kind: 'idle' }), 1800);
  };

  const updateProfile = (patch: Partial<UserProfile>) =>
    setSettings((s) => ({ ...s, profile: { ...s.profile, ...patch } }));
  const updateSignature = (patch: Partial<UserSignature>) =>
    setSettings((s) => ({ ...s, signature: { ...s.signature, ...patch } }));
  const updatePayment = (patch: Partial<PaymentMethod>) =>
    setSettings((s) => ({ ...s, payment: { ...s.payment, ...patch } }));

  const saveSection = (section: string) => {
    try {
      saveSettings(settings);
      flash(section);
    } catch (e) {
      setStatus({
        kind: 'error',
        msg: e instanceof Error ? e.message : 'Could not save',
      });
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <Masthead
        kicker="Account"
        title="Settings"
        dateline="Workspace preferences · Stored on this device"
        lede="Your personal details, email signature, billing on file, and account controls. Changes save to this browser only until you connect your workspace to a backend."
      />

      {status.kind === 'saved' && (
        <div className="flex items-center gap-2 rounded-xl border border-success-border bg-success-soft px-4 py-2.5 text-[0.8125rem] text-success-bold">
          <CheckCircle2 className="h-4 w-4" />
          {status.section} saved.
        </div>
      )}
      {status.kind === 'error' && (
        <div className="flex items-center gap-2 rounded-xl border border-danger-border bg-danger-soft px-4 py-2.5 text-[0.8125rem] text-danger-bold">
          <AlertTriangle className="h-4 w-4" />
          {status.msg}
        </div>
      )}

      {/* Agent Ledger link (sub-page) */}
      <Link
        to="/settings/ledger"
        className="group card flex items-center gap-4 p-6 transition hover:border-accent-border hover:bg-accent-soft"
      >
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent-bold ring-1 ring-accent-border">
          <LineChart className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[0.9375rem] font-semibold text-ink">Agent Ledger</div>
          <div className="text-[0.8125rem] text-ink-dim">
            Monthly spend, per-agent cost, and the global kill switch.
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-ink-faint transition group-hover:translate-x-0.5 group-hover:text-ink" />
      </Link>

      {/* Profile */}
      <SettingsSection
        icon={UserRound}
        title="Profile"
        description="The full identity we use on outreach letterheads and Notion records."
        onSave={() => saveSection('Profile')}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <LabeledField label="Full name">
            <Input
              value={settings.profile.full_name}
              onChange={(e) => updateProfile({ full_name: e.target.value })}
              placeholder="Ada Lovelace"
            />
          </LabeledField>
          <LabeledField label="Work email">
            <Input
              type="email"
              value={settings.profile.email}
              onChange={(e) => updateProfile({ email: e.target.value })}
              placeholder="you@company.com"
            />
          </LabeledField>
          <LabeledField label="Phone">
            <Input
              value={settings.profile.phone}
              onChange={(e) => updateProfile({ phone: e.target.value })}
              placeholder="+1 (202) 555-0134"
            />
          </LabeledField>
          <LabeledField label="Title">
            <Input
              value={settings.profile.title}
              onChange={(e) => updateProfile({ title: e.target.value })}
              placeholder="Director, Federal Real Estate"
            />
          </LabeledField>
          <LabeledField label="Company" className="sm:col-span-2">
            <Input
              value={settings.profile.company}
              onChange={(e) => updateProfile({ company: e.target.value })}
              placeholder="Occasion Crafters LLC"
            />
          </LabeledField>
          <LabeledField label="Street address" className="sm:col-span-2">
            <Input
              value={settings.profile.address_line1}
              onChange={(e) => updateProfile({ address_line1: e.target.value })}
              placeholder="1455 Pennsylvania Ave NW"
            />
          </LabeledField>
          <LabeledField label="Suite / unit" className="sm:col-span-2">
            <Input
              value={settings.profile.address_line2}
              onChange={(e) => updateProfile({ address_line2: e.target.value })}
              placeholder="Suite 400"
            />
          </LabeledField>
          <LabeledField label="City">
            <Input
              value={settings.profile.city}
              onChange={(e) => updateProfile({ city: e.target.value })}
            />
          </LabeledField>
          <div className="grid grid-cols-[1fr_1fr] gap-3">
            <LabeledField label="State">
              <Input
                value={settings.profile.state}
                onChange={(e) => updateProfile({ state: e.target.value })}
                maxLength={2}
              />
            </LabeledField>
            <LabeledField label="ZIP">
              <Input
                value={settings.profile.zip}
                onChange={(e) => updateProfile({ zip: e.target.value })}
              />
            </LabeledField>
          </div>
        </div>
      </SettingsSection>

      {/* Email signature */}
      <SettingsSection
        icon={Mail}
        title="Email signature"
        description="Appended to every broker outreach draft. Preview updates as you type."
        onSave={() => saveSection('Signature')}
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1fr]">
          <div className="flex flex-col gap-3">
            <LabeledField label="Signature">
              <Textarea
                value={settings.signature.body}
                onChange={(e) => updateSignature({ body: e.target.value })}
                className="min-h-[180px] font-mono text-[0.8125rem]"
                placeholder={'Your name\nYour title · Your company\nPhone · Email'}
              />
            </LabeledField>
            <div className="flex flex-col gap-2 text-[0.8125rem]">
              <CheckboxRow
                checked={settings.signature.include_company}
                onChange={(v) => updateSignature({ include_company: v })}
                label="Append company name automatically"
              />
              <CheckboxRow
                checked={settings.signature.include_phone}
                onChange={(v) => updateSignature({ include_phone: v })}
                label="Append phone number automatically"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="kicker">Preview</div>
            <div className="flex min-h-[180px] flex-col gap-1 rounded-xl border border-rule bg-surface-sunk p-5 text-[0.8125rem] leading-relaxed text-ink">
              <div className="text-ink-muted">— — —</div>
              <pre className="whitespace-pre-wrap font-sans text-[0.8125rem] leading-relaxed text-ink">
{settings.signature.body}
{settings.signature.include_company && settings.profile.company
  ? `\n${settings.profile.company}`
  : ''}
{settings.signature.include_phone && settings.profile.phone
  ? `\n${settings.profile.phone}`
  : ''}
              </pre>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Payment */}
      <SettingsSection
        icon={CreditCard}
        title="Payment method"
        description="Card of record for workspace billing. We store only the last four digits."
        onSave={() => saveSection('Payment')}
      >
        <div className="flex flex-col gap-5">
          <CardPreview payment={settings.payment} profile={settings.profile} />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <LabeledField label="Cardholder name">
              <Input
                value={settings.payment.cardholder}
                onChange={(e) => updatePayment({ cardholder: e.target.value })}
                placeholder={settings.profile.full_name}
              />
            </LabeledField>
            <LabeledField label="Card brand">
              <select
                className="flex h-10 w-full rounded-lg border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10"
                value={settings.payment.brand}
                onChange={(e) =>
                  updatePayment({ brand: e.target.value as PaymentMethod['brand'] })
                }
              >
                <option value="">Select…</option>
                <option value="visa">Visa</option>
                <option value="mastercard">Mastercard</option>
                <option value="amex">American Express</option>
                <option value="discover">Discover</option>
              </select>
            </LabeledField>
            <LabeledField label="Last 4 digits">
              <Input
                inputMode="numeric"
                maxLength={4}
                pattern="[0-9]{4}"
                value={settings.payment.last4}
                onChange={(e) =>
                  updatePayment({ last4: e.target.value.replace(/\D/g, '').slice(0, 4) })
                }
                placeholder="4242"
              />
            </LabeledField>
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-3">
              <LabeledField label="Exp month">
                <Input
                  inputMode="numeric"
                  maxLength={2}
                  placeholder="MM"
                  value={settings.payment.exp_month}
                  onChange={(e) =>
                    updatePayment({
                      exp_month: e.target.value.replace(/\D/g, '').slice(0, 2),
                    })
                  }
                />
              </LabeledField>
              <LabeledField label="Exp year">
                <Input
                  inputMode="numeric"
                  maxLength={4}
                  placeholder="YYYY"
                  value={settings.payment.exp_year}
                  onChange={(e) =>
                    updatePayment({
                      exp_year: e.target.value.replace(/\D/g, '').slice(0, 4),
                    })
                  }
                />
              </LabeledField>
              <LabeledField label="Billing ZIP">
                <Input
                  value={settings.payment.billing_zip}
                  onChange={(e) => updatePayment({ billing_zip: e.target.value })}
                />
              </LabeledField>
            </div>
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-info-border bg-info-soft px-3 py-2.5 text-[0.75rem] text-info-bold">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Demo card capture only — do not enter a real card number. Connect Stripe to
              take payments in production.
            </span>
          </div>
        </div>
      </SettingsSection>

      {/* Invited teammates (owner only) */}
      {isOwner(getSession()?.email) && <InviteSection />}

      {/* Organization — logo + name shown in the sidebar card */}
      <SettingsSection
        icon={Building2}
        title="Organization"
        description="Your hospital, school, or agency logo and name — shown in the sidebar card and on outreach letterhead."
        onSave={() => saveSection('Organization')}
      >
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[auto_1fr]">
          <OrgLogoField
            logo={settings.profile.organization_logo_data_url}
            name={settings.profile.organization_name}
            onChange={(dataUrl) =>
              updateProfile({ organization_logo_data_url: dataUrl })
            }
            onClear={() => updateProfile({ organization_logo_data_url: '' })}
          />
          <div className="flex flex-col gap-4">
            <LabeledField label="Organization name">
              <Input
                value={settings.profile.organization_name}
                onChange={(e) => updateProfile({ organization_name: e.target.value })}
                placeholder="University of Utah"
                data-testid="org-name"
              />
            </LabeledField>
            <LabeledField label="Tagline">
              <Input
                value={settings.profile.organization_tagline}
                onChange={(e) =>
                  updateProfile({ organization_tagline: e.target.value })
                }
                placeholder="University Hospital · UT"
                data-testid="org-tagline"
              />
            </LabeledField>
            <p className="text-[0.6875rem] text-ink-muted">
              Square PNG or SVG works best. Keep it under 500 KB so the sidebar stays
              snappy.
            </p>
          </div>
        </div>
      </SettingsSection>

      {/* Danger zone */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-danger-bold" />
          <h2 className="text-[0.9375rem] font-semibold text-ink">Danger zone</h2>
        </div>
        <div className="rounded-xl border border-danger-border bg-surface p-5">
          <DeleteAccountRow
            onDelete={() => {
              clearSettings();
              navigate('/');
            }}
          />
        </div>
      </section>
    </div>
  );
}

function SettingsSection({
  icon: Icon,
  title,
  description,
  children,
  onSave,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  children: React.ReactNode;
  onSave: () => void;
}) {
  return (
    <section className="card flex flex-col gap-5 p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-50 text-brand-700">
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-[0.9375rem] font-semibold text-ink">{title}</h2>
            <p className="text-xs text-ink-muted">{description}</p>
          </div>
        </div>
        <Button size="sm" onClick={onSave}>
          <Save className="h-3.5 w-3.5" /> Save
        </Button>
      </div>
      <div className="border-t border-rule pt-5">{children}</div>
    </section>
  );
}

function LabeledField({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cn('flex flex-col gap-1.5', className)}>
      <span className="kicker">{label}</span>
      {children}
    </label>
  );
}

function CheckboxRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-ink-muted">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-rule-strong text-brand-600 focus:ring-brand-500"
      />
      <span>{label}</span>
    </label>
  );
}

function CardPreview({
  payment,
  profile,
}: {
  payment: PaymentMethod;
  profile: UserProfile;
}) {
  const brandLabel = payment.brand
    ? { visa: 'VISA', mastercard: 'MASTERCARD', amex: 'AMEX', discover: 'DISCOVER' }[
        payment.brand
      ]
    : 'CARD';
  const last4 = payment.last4 || '••••';
  const expMonth = payment.exp_month || 'MM';
  const expYear = payment.exp_year ? payment.exp_year.slice(-2) : 'YY';
  return (
    <div className="relative h-48 w-full max-w-md overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-brand-800 p-6 text-white shadow-card">
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(circle at 0% 0%, rgba(255,255,255,0.25), transparent 45%), radial-gradient(circle at 100% 100%, rgba(45,212,191,0.35), transparent 40%)',
        }}
        aria-hidden
      />
      <div className="relative flex h-full flex-col justify-between">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-white/70">
              Workspace card
            </div>
            <div className="mt-1 text-[0.8125rem] text-white/85">RLPScout Billing</div>
          </div>
          <div className="text-[0.625rem] font-bold uppercase tracking-[0.2em] text-white/90">
            {brandLabel}
          </div>
        </div>
        <div className="font-mono text-[1.375rem] tracking-[0.3em]">
          •••• •••• •••• {last4}
        </div>
        <div className="flex items-end justify-between text-[0.75rem]">
          <div>
            <div className="text-[0.5625rem] uppercase tracking-[0.14em] text-white/60">
              Cardholder
            </div>
            <div className="font-semibold">
              {payment.cardholder || profile.full_name || '—'}
            </div>
          </div>
          <div>
            <div className="text-[0.5625rem] uppercase tracking-[0.14em] text-white/60">
              Expires
            </div>
            <div className="font-mono font-semibold">
              {expMonth}/{expYear}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrgLogoField({
  logo,
  name,
  onChange,
  onClear,
}: {
  logo: string;
  name: string;
  onChange: (dataUrl: string) => void;
  onClear: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const initials = (() => {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '·';
    if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
    return (words[0]![0]! + words[1]![0]!).toUpperCase();
  })();

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please pick an image file.');
      return;
    }
    if (file.size > 500 * 1024) {
      setError('That file is over 500 KB — try a smaller logo.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') onChange(result);
    };
    reader.onerror = () => setError('Could not read that file.');
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="grid h-24 w-24 place-items-center overflow-hidden rounded-2xl border border-rule bg-surface-sunk shadow-card">
        {logo ? (
          <img
            src={logo}
            alt={name || 'Organization logo'}
            className="h-full w-full object-contain p-2"
          />
        ) : (
          <span className="text-2xl font-bold tracking-wide text-ink-muted">
            {initials}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-rule-strong bg-surface px-3 py-2 text-[0.8125rem] font-semibold text-ink shadow-card hover:border-brand-400 hover:text-brand-700">
          <ImagePlus className="h-3.5 w-3.5" />
          {logo ? 'Replace' : 'Upload logo'}
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid="org-logo-upload"
          />
        </label>
        {logo && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            data-testid="org-logo-clear"
          >
            <X className="h-3.5 w-3.5" /> Remove
          </Button>
        )}
      </div>
      {error && (
        <p className="text-[0.75rem] text-danger-bold" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function InviteSection() {
  const [invites, setInvites] = useState<string[]>(() => getInvites());
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      setError('Enter a valid email.');
      return;
    }
    if (trimmed === OWNER_EMAIL) {
      setError('That’s the workspace owner — already has access.');
      return;
    }
    if (invites.includes(trimmed)) {
      setError('Already invited.');
      return;
    }
    setInvites(addInvite(trimmed));
    setEmail('');
  };

  const onRemove = (target: string) => {
    setInvites(removeInvite(target));
  };

  return (
    <section className="card flex flex-col gap-5 p-6">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-lg bg-brand-50 text-brand-700">
          <UserPlus className="h-4 w-4" />
        </div>
        <div>
          <h2 className="text-[0.9375rem] font-semibold text-ink">Invited teammates</h2>
          <p className="max-w-xl text-xs text-ink-muted">
            Only these emails — plus the owner ({OWNER_EMAIL}) — can sign in. Anyone else
            is locked out at the login screen.
          </p>
        </div>
      </div>
      <div className="border-t border-rule pt-5">
        <form onSubmit={onAdd} className="flex flex-wrap items-end gap-3">
          <label className="flex flex-1 flex-col gap-1.5 min-w-[240px]">
            <span className="kicker">Invite by email</span>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@company.com"
              data-testid="invite-email"
            />
          </label>
          <Button type="submit" size="sm" data-testid="invite-add">
            <UserPlus className="h-3.5 w-3.5" /> Invite
          </Button>
        </form>
        {error && (
          <p className="mt-2 text-[0.75rem] text-danger-bold" role="alert">
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-col gap-2">
          <div className="flex items-center justify-between rounded-lg border border-rule bg-surface-sunk px-3 py-2">
            <div className="flex items-center gap-3">
              <div className="grid h-7 w-7 place-items-center rounded-full bg-brand-100 text-[0.625rem] font-bold uppercase text-brand-700">
                OW
              </div>
              <div className="text-[0.8125rem] text-ink">{OWNER_EMAIL}</div>
            </div>
            <span className="kicker text-brand-700">Owner</span>
          </div>
          {invites.length === 0 ? (
            <p className="text-[0.75rem] text-ink-muted">
              No teammates invited yet. Just you for now.
            </p>
          ) : (
            invites.map((inv) => (
              <div
                key={inv}
                className="flex items-center justify-between rounded-lg border border-rule bg-surface px-3 py-2"
                data-testid={`invite-row-${inv}`}
              >
                <div className="flex items-center gap-3">
                  <div className="grid h-7 w-7 place-items-center rounded-full bg-ink-dim/15 text-[0.625rem] font-bold uppercase text-ink-muted">
                    {inv.slice(0, 2)}
                  </div>
                  <div className="text-[0.8125rem] text-ink">{inv}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(inv)}
                  className="grid h-7 w-7 place-items-center rounded-md text-ink-muted hover:bg-danger-soft hover:text-danger-bold"
                  aria-label={`Revoke invite for ${inv}`}
                  data-testid={`invite-remove-${inv}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function DeleteAccountRow({ onDelete }: { onDelete: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [text, setText] = useState('');
  const canDelete = text.trim().toUpperCase() === 'DELETE';
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-[0.875rem] font-semibold text-ink">Delete account</h3>
          <p className="mt-0.5 max-w-xl text-xs text-ink-muted">
            Remove all locally-stored workspace preferences from this browser. Server-side
            records, sent emails, and broker history remain untouched.
          </p>
        </div>
        {!confirming ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirming(true)}
            data-testid="delete-account"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete my data
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        )}
      </div>

      {confirming && (
        <div className="flex flex-col gap-3 rounded-lg border border-danger-border bg-danger-soft p-4">
          <p className="text-[0.8125rem] font-medium text-danger-bold">
            Type <span className="font-mono font-bold">DELETE</span> to confirm. This
            clears your profile, signature, and saved payment method from this browser.
          </p>
          <div className="flex flex-wrap gap-2">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type DELETE"
              className="max-w-xs border-danger-border bg-surface"
              data-testid="delete-confirm-input"
            />
            <Button
              variant="destructive"
              size="sm"
              disabled={!canDelete}
              onClick={onDelete}
              data-testid="delete-confirm-button"
            >
              Permanently delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

